/**
 * Address autocomplete client. Talks only to our own /api/geocode proxy, never
 * to a map provider directly — the Mapbox token lives on the server, and which
 * provider actually answered is the proxy's business, not this file's.
 *
 * Suggestions always arrive complete, coordinates included, whichever provider
 * served them. That is one of the reasons the proxy uses Mapbox's Geocoding API
 * rather than Search Box: Search Box would hand back labels without coordinates
 * and require a second "retrieve" call for whichever row the user clicked,
 * which the caller would then have to sequence around.
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

/**
 * Suggestions for a partial address, each with `label`, `kind`, `lat`, `lng`.
 *
 * Resolves to an empty list on any failure, including an aborted request. The
 * proxy already swallows provider outages, so a failure here means our own API
 * is unreachable — and a typeahead that quietly shows nothing is much kinder
 * than one that throws over a half-filled form. The address input stays
 * free-text either way, so the worst case is an address with no coordinates.
 */
export async function searchAddresses(query, { biasLat, biasLon, signal } = {}) {
  const params = new URLSearchParams({ q: query, limit: '8' });
  if (biasLat != null && biasLon != null) {
    params.set('lat', String(biasLat));
    params.set('lon', String(biasLon));
  }

  try {
    const res = await fetch(`${API_BASE_URL}/geocode/suggest?${params}`, { signal });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.results) ? data.results : [];
  } catch {
    return [];
  }
}
